import { TeamTemplateSize } from "state/entities/teamTemplateSizes/teamTemplateSizesActions";

interface DesignLike {
  templateCode?: string;
  templateSize?: string;
  subtitle?: string;
  width?: number;
  height?: number;
  widthInMm?: number;
  heightInMm?: number;
  widthInInches?: number;
  heightInInches?: number;
}

// Backend computes widthIn{Mm,Inches} on team sizes at 96 DPI; we mirror
// that here to convert a team size's stored px width back to its native unit.
const PX_PER_INCH = 96;
const PX_PER_MM = PX_PER_INCH / 25.4;

// Designs and team sizes go through different rounding paths even when the
// user-meaningful dimensions are identical, so compare in the team size's
// native unit. Tolerance only needs to cover sub-unit DPI drift — the integer
// round-trip cases already snap exactly in teamSizeInItsUnit.
const TOLERANCE: Record<TeamTemplateSize["measurementUnits"], number> = {
  px: 1,
  mm: 0.1,
  in: 0.005
};

export const isCustomDesign = (design: DesignLike): boolean => {
  if (design.templateCode === "custom") return true;
  // Some flows leave templateCode undefined but set templateSize to "Custom".
  const label = (design.templateSize || "").toLowerCase();
  return label === "custom";
};

// A custom design's subtitle is "default" when the user has not renamed it —
// empty, still equal to the templateSize, or the literal "custom". A default
// subtitle can be replaced with a better label (team size name / measurements);
// anything the user typed themselves is preserved.
export const subtitleLooksDefault = (design: DesignLike): boolean => {
  const subtitle = design.subtitle;
  if (!subtitle) return true;
  if (subtitle === design.templateSize) return true;
  return subtitle.toLowerCase() === "custom";
};

// Convert a team size's stored px width to its display unit. Snaps integer
// inputs back to the same integer (e.g. 1200mm → 4535px → 1200mm) so the
// comparison stays exact for the common case.
const teamSizeInItsUnit = (
  px: number,
  unit: TeamTemplateSize["measurementUnits"]
): number => {
  if (unit === "px") return px;
  if (unit === "mm") {
    const mm = px / PX_PER_MM;
    const asInt = Math.round(mm);
    if (Math.abs(asInt * PX_PER_MM - px) < 1) return asInt;
    return Math.round(mm * 100) / 100;
  }
  // inches
  const inches = px / PX_PER_INCH;
  return Math.round(inches * 1000) / 1000;
};

const designValueInUnit = (
  design: DesignLike,
  unit: TeamTemplateSize["measurementUnits"],
  axis: "width" | "height"
): number | undefined => {
  if (unit === "px") return design[axis];
  if (unit === "mm")
    return axis === "width" ? design.widthInMm : design.heightInMm;
  return axis === "width" ? design.widthInInches : design.heightInInches;
};

const matchDeltaForDesign = (
  size: TeamTemplateSize,
  design: DesignLike
): number | null => {
  const unit = size.measurementUnits;
  const tolerance = TOLERANCE[unit] ?? 1;
  const sizeWidth = teamSizeInItsUnit(size.width, unit);
  const sizeHeight = teamSizeInItsUnit(size.height, unit);
  const designWidth = designValueInUnit(design, unit, "width");
  const designHeight = designValueInUnit(design, unit, "height");
  if (typeof designWidth !== "number" || typeof designHeight !== "number") {
    return null;
  }
  const widthDelta = Math.abs(sizeWidth - designWidth);
  const heightDelta = Math.abs(sizeHeight - designHeight);
  if (widthDelta > tolerance || heightDelta > tolerance) return null;
  return widthDelta + heightDelta;
};

export const findMatchingTeamSize = (
  design: DesignLike | null | undefined,
  teamSizes: TeamTemplateSize[] | null | undefined
): TeamTemplateSize | null => {
  if (!design || !isCustomDesign(design)) return null;
  if (!teamSizes || teamSizes.length === 0) return null;
  // When multiple sizes fall within tolerance, prefer the closest — otherwise
  // `createdAt DESC` order would silently relabel a design as the most-recently
  // created near-duplicate.
  let best: TeamTemplateSize | null = null;
  let bestDelta = Infinity;
  for (const size of teamSizes) {
    const delta = matchDeltaForDesign(size, design);
    if (delta !== null && delta < bestDelta) {
      best = size;
      bestDelta = delta;
    }
  }
  return best;
};

export const getTeamSizeNameForDesign = (
  design: DesignLike | null | undefined,
  teamSizes: TeamTemplateSize[] | null | undefined
): string | null => {
  const match = findMatchingTeamSize(design, teamSizes);
  return match ? match.name : null;
};
